import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { number, object } from "valibot";
import { dataName, integerValue, label, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { NumberInput } from "../../components/inputs/NumberInput";
import { MAX_RATE_SCALE, MIN_RATE_SCALE, NUMBER_RANGE_CONDITION_KEYS } from "../../constants";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { PreviewContentLabel, RatingPreview } from "../FieldPreviewComponents";
import { keyFromSelector } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  required: required(),
  rateScale: integerValue(MIN_RATE_SCALE, MAX_RATE_SCALE),
});
const ValueSchema = number();

export type RatingFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const RatingField: Field<RatingFieldProperties, DefaultDtoType, ValueType> = {
  type: "rating",
  category: "choices",
  widget: "com.moreapps.plugins:rating:10",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({ required: false, rateScale: 5 }),
  ruleBehaviour: {
    conditions: { keys: NUMBER_RANGE_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: {
        fieldTypes: ["calculation", "lookup", "number", "radio", "rating", "search", "slider", "smiley"],
        variableTypes: ["number"],
      },
    },
  },
  filter: {
    type: "number",
    definition: [
      { operator: "is", inputType: "number", allowDecimal: false, schema: integerValue(1, 10) },
      { operator: "isNot", inputType: "number", allowDecimal: false, schema: integerValue(1, 10) },
      { operator: "greaterThan", inputType: "number", allowDecimal: false, schema: integerValue(1, 10) },
      { operator: "lessThan", inputType: "number", allowDecimal: false, schema: integerValue(1, 10) },
      isFilledInFilter,
    ],
  },
  stringValue: (value) => value.toString(),
  renderValue: (value) => value,
  icon: "star",
  titleKey: keyFromSelector(($) => $.RATING_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.RATING_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    rateScale: keyFromSelector(($) => $.FIELD_PROPERTY_RATE_SCALE),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <RatingPreview rateScale={props.properties.rateScale} />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <NumberInput
            label={t(props.propertyKeys.rateScale)}
            value={props.data.properties.rateScale}
            minValue={2}
            maxValue={10}
            required
            onBlur={(value) => props.setValue("rateScale", value)}
            errorMessage={props.getError("rateScale")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(RatingField);
